import React from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

export const SurveyStep = ({
  question,
  index,
  total,
  value,
  onChange,
  onNext,
  onBack,
  canBack,
}) => {
  const progress = ((index + 1) / total) * 100;
  const isLast = index === total - 1;

  return (
    <section
      className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
      data-testid={`survey-step-${question.id}`}
    >
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3 text-xs font-bold tracking-[0.18em] uppercase">
          <span className="text-[#63499d]">
            Question {index + 1} of {total}
          </span>
          <span className="text-slate-400">{Math.round(progress)}%</span>
        </div>
        <div className="progress-track" data-testid="survey-progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="rc-fade-up bg-white border border-slate-200 rounded-3xl shadow-xl shadow-[#63499d]/10 p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="gold-bar" />
          <span className="text-[#63499d] text-xs font-bold tracking-[0.18em] uppercase">
            Step {index + 1}
          </span>
        </div>
        <h2
          className="font-display text-2xl sm:text-3xl text-[#2a1f4a] leading-tight mb-2"
          data-testid="question-title"
        >
          {question.title}
        </h2>
        {question.subtitle && (
          <p className="text-sm text-slate-500 mb-6 leading-relaxed">
            {question.subtitle}
          </p>
        )}

        <div className="space-y-3 mt-6" role="radiogroup" aria-labelledby="question-title">
          {question.options.map((opt) => {
            const selected = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onChange(opt.value)}
                className={`answer-card w-full text-left flex items-center gap-4 p-4 sm:p-5 bg-white border-2 border-slate-200 rounded-2xl ${
                  selected ? "selected" : ""
                }`}
                data-testid={`answer-${question.id}-${opt.value}`}
              >
                <div
                  className={`w-6 h-6 shrink-0 rounded-full border-2 grid place-items-center transition-colors ${
                    selected
                      ? "bg-[#63499d] border-[#63499d] text-white"
                      : "border-slate-300 text-transparent"
                  }`}
                >
                  <Check className="w-3.5 h-3.5" />
                </div>
                <div className="font-semibold text-[#2a1f4a] text-base sm:text-lg leading-snug">
                  {opt.label}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-9 flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={onBack}
            disabled={!canBack}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-[#63499d] disabled:opacity-30 hover:text-[#4d3884] transition-colors"
            data-testid="survey-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            type="button"
            onClick={onNext}
            disabled={!value}
            className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold"
            data-testid="survey-next-button"
          >
            {isLast ? "See my recommendation" : "Next"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default SurveyStep;
